/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { FiSend, FiLayers, FiBox } from "react-icons/fi";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db_modular } from "../../firebaseConfig";
import { SHA256 } from "crypto-js";
import { signTransaction } from "../CreateRoom";
import toast from "react-hot-toast";

function CreateTransactionComponent({ user, userData, roomId, team }) {
  const [receiverPK, setReceiverPK] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [amount, setAmount] = useState("");
  const [isSigned, setIsSigned] = useState(false);
  const [signature, setSignature] = useState(null);
  const [lastHash, setLastHash] = useState(null);

  const handleSign = async () => {
    if (!receiverPK || !amount || amount <= 0 || !privateKey) {
      toast.error("Please fill all fields correctly");
      return;
    }
    try {
      const txData = {
        sender: userData?.publicKey,
        senderEmail: user?.email,
        receiver: receiverPK,
        nonce: Math.floor(Math.random() * 1000),
        amount: Number(amount),
        status: "Verify",
      };
      const signed = await signTransaction(txData, privateKey);
      setSignature(signed);
      setIsSigned(true);
      toast.success("Transaction Signed Successfully!");
    } catch (error) {
      toast.error("Failed to sign transaction");
    }
  };

  const handleSend = async () => {
    if (!isSigned || !signature) return;
    try {
      const txData = {
        sender: userData?.publicKey,
        senderEmail: user?.email,
        receiver: receiverPK,
        nonce: Math.floor(Math.random() * 1000),
        amount: Number(amount),
        status: "Verify",
        signature,
      };
      const hash = SHA256(JSON.stringify(txData)).toString();
      await addDoc(collection(db_modular, "rooms", roomId, `${team}TeamTransactions`), {
        ...txData,
        hash,
        timestamp: serverTimestamp(),
      });
      toast.success("Transaction Broadcasted!");
      setReceiverPK("");
      setPrivateKey("");
      setAmount("");
      setIsSigned(false);
      setLastHash(hash);
    } catch (error) {
      toast.error("Failed to send transaction");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div className="space-y-6">
        <div>
          <h3 className="text-2xl font-bold text-white flex items-center gap-3">
            <FiSend className="text-primary" /> Create Transaction
          </h3>
          <p className="text-text-muted mt-1">
            Send funds securely within your team network. (Opposite team members restricted)
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-text-muted uppercase ml-1">
              Receiver Public Key
            </label>
            <input
              type="text"
              className="w-full bg-surface border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary transition-all font-mono"
              placeholder="Paste receiver's public key"
              value={receiverPK}
              onChange={(e) => {
                setReceiverPK(e.target.value);
                setIsSigned(false);
              }}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-text-muted uppercase ml-1">
                Your Private Key
              </label>
              <input
                type="password"
                className="w-full bg-surface border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary transition-all font-mono"
                placeholder="Required for signing"
                value={privateKey}
                onChange={(e) => {
                  setPrivateKey(e.target.value);
                  setIsSigned(false);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-text-muted uppercase ml-1">
                Amount (ASTRA)
              </label>
              <input
                type="number"
                className="w-full bg-surface border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary transition-all"
                placeholder="0.00"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setIsSigned(false);
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-4 pt-2">
          {!isSigned ? (
            <button
              onClick={handleSign}
              className="flex-1 bg-primary text-white font-bold py-4 rounded-xl hover:bg-primary-dark shadow-lg shadow-primary/20 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              <FiLayers /> Sign Transaction
            </button>
          ) : (
            <button
              onClick={handleSend}
              className="flex-1 bg-green-500 text-white font-bold py-4 rounded-xl hover:bg-green-600 shadow-lg shadow-green-500/20 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              <FiSend /> Broadcast Transaction
            </button>
          )}
        </div>
      </div>

      <div className="hidden lg:block">
        <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-3xl p-8 border border-white/10 relative overflow-hidden group">
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/20 blur-[100px] rounded-full"></div>
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-secondary/20 blur-[100px] rounded-full"></div>

          <div className="relative z-10 space-y-6">
            <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-primary">
              <FiBox size={24} />
            </div>
            <h4 className="text-xl font-bold text-white">Transaction Helper</h4>
            <ul className="space-y-4 text-text-muted text-sm">
              <li className="flex gap-3">
                <div className="mt-1 w-1.5 h-1.5 rounded-full bg-primary shrink-0"></div>
                Transactions are restricted to members of your own team only.
              </li>
              <li className="flex gap-3">
                <div className="mt-1 w-1.5 h-1.5 rounded-full bg-primary shrink-0"></div>
                Transactions are signed using your private key but it's never stored or transmitted.
              </li>
              <li className="flex gap-3">
                <div className="mt-1 w-1.5 h-1.5 rounded-full bg-secondary shrink-0"></div>
                Once signed, broadcast your transaction to send it to the mempool for mining.
              </li>
              <li className="flex gap-3">
                <div className="mt-1 w-1.5 h-1.5 rounded-full bg-green-500 shrink-0"></div>
                Miners will pick up your transaction and include it in the next block.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateTransactionComponent;
